<script setup>
import { useLanguageStore } from "@/stores/languageStore.js";
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();
const languageStore = useLanguageStore();

const activeTab = computed(() => {
  if (route.name === "level-map-selector" || route.name === "custom-level-editor") return "levels";
  if (route.name === "map-manager") return "manage";
  return "star";
});

function go(tab) {
  if (tab === "star") {
    router.push({ name: "star-map-editor" });
    return;
  }
  if (tab === "levels") {
    router.push({ name: "level-map-selector" });
    return;
  }
  router.push({ name: "map-manager" });
}
</script>

<template>
  <nav class="star-workshop-nav" :aria-label="languageStore.t('creativeWorkshop')">
    <button type="button" :class="{ active: activeTab === 'star' }" @click="go('star')">
      <span class="tab-icon" aria-hidden="true">✦</span>
      <span class="tab-copy">
        <strong>{{ languageStore.t("workshopStarMap") }}</strong>
</span>
    </button>
    <button type="button" :class="{ active: activeTab === 'levels' }" @click="go('levels')">
      <span class="tab-icon grid" aria-hidden="true">▦</span>
      <span class="tab-copy">
        <strong>{{ languageStore.t("workshopLevelMap") }}</strong>
</span>
    </button>
    <button type="button" :class="{ active: activeTab === 'manage' }" @click="go('manage')">
      <span class="tab-icon" aria-hidden="true">☰</span>
      <span class="tab-copy">
        <strong>{{ languageStore.t("myCustomMaps") }}</strong>
</span>
    </button>
  </nav>
</template>

<style scoped>
.star-workshop-nav {
  width: min(100%, 820px);
  margin: 0 auto 16px;
  padding: 5px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 5px;
  border: 1px solid rgba(82, 246, 232, .17);
  border-radius: 17px;
  background: rgba(3, 21, 35, .72);
  box-shadow: inset 0 0 18px rgba(82, 246, 232, .025);
}
.star-workshop-nav button {
  min-width: 0;
  min-height: 58px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid transparent;
  border-radius: 12px;
  color: rgba(230, 246, 248, .62);
  background: transparent;
  transition: border-color .16s ease, background .16s ease, color .16s ease, transform .16s ease;
}
.star-workshop-nav button:hover {
  color: #eafffd;
  background: rgba(20, 70, 82, .32);
}
.star-workshop-nav button.active {
  color: #fff;
  border-color: rgba(82, 246, 232, .55);
  background: linear-gradient(180deg, rgba(17, 82, 95, .76), rgba(5, 42, 57, .84));
  box-shadow: inset 0 0 18px rgba(82, 246, 232, .06);
}
.tab-icon {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  color: #82fff5;
  font-size: 20px;
  line-height: 1;
}
.tab-icon.grid { font-size: 23px; }
.tab-copy {
  min-width: 0;
  display: grid;
  gap: 2px;
  text-align: left;
}
.tab-copy strong {
  font-size: 12px;
  line-height: 1.1;
  white-space: nowrap;
}
.tab-copy small {
  overflow: hidden;
  color: rgba(255, 255, 255, .39);
  font-size: 8px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.star-workshop-nav button.active .tab-copy small { color: rgba(225, 255, 252, .62); }
@media (max-width: 560px) {
  .star-workshop-nav {
    margin-bottom: 8px;
    padding: 3px;
    gap: 3px;
    border-radius: 12px;
  }
  .star-workshop-nav button {
    min-height: 42px;
    max-height: 42px;
    padding: 4px 3px;
    gap: 3px;
    border-radius: 9px;
    overflow: hidden;
  }
  .tab-icon {
    width: 17px;
    height: 17px;
    font-size: 14px;
  }
  .tab-icon.grid { font-size: 16px; }
  .tab-copy {
    min-width: 0;
    display: block;
    line-height: 1;
  }
  .tab-copy strong {
    display: block;
    max-width: 100%;
    overflow: hidden;
    font-size: 15px;
    line-height: 1.05;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .tab-copy small { display: none; }
}

@media (max-width: 380px) {
  .star-workshop-nav button {
    min-height: 40px;
    max-height: 40px;
  }
  .tab-icon { width: 15px; height: 15px; font-size: 12px; }
  .tab-icon.grid { font-size: 14px; }
  .tab-copy strong { font-size: 13px; }
}
</style>
